/**
 * دو چیزی که باید از یک «رفت‌وبرگشتِ ورود» جان به در ببرند:
 * «بعدش کجا برگردم» و «این ورود، تاییدِ حذفِ حساب بود».
 *
 * ═══ چرا اصلاً لازم است ═══
 *
 * ورود همیشه یک تعویضِ صفحه نیست. با گوگل/اپل مرورگر کلاً از سایت می‌رود و
 * با یک آدرسِ **ثابت** برمی‌گردد (`redirectTo` در LoginPage، و روی موبایل
 * یک deep link). یعنی هر چیزی که در حافظه‌ی صفحه بود — از جمله «از کجا
 * آمده بودم» — همان‌جا می‌ماند و می‌رود. نتیجه‌اش این بود: کسی که از
 * صفحه‌ی عمومیِ حذفِ حساب روی «وارد شو» می‌زد، بعد از ورود سر از صفحه‌ی
 * اول درمی‌آورد و باید دوباره خودش آن صفحه را پیدا می‌کرد.
 *
 * پس مقصد **روی دستگاه** نوشته می‌شود، نه در آدرس؛ و چون یک‌بارمصرف است،
 * خواندنش همان‌جا پاکش می‌کند.
 *
 * ═══ نشانِ «دوباره وارد شد» ═══
 *
 * حذفِ حساب برگشت ندارد، پس قبلش یک‌بار دیگر پرسیده می‌شود «واقعاً خودتی؟»
 * — با همان کاری که هر کسی بلد است: خروج، و ورودِ دوباره. `armDeleteReauth`
 * درست قبل از خروج زده می‌شود و `takeDeleteReauth` بعد از برگشت.
 *
 * ⚠ این نشان چه چیزی را ثابت **نمی‌کند**: کسی که همین حالا دستش به دستگاهِ
 * بازِ کاربر است می‌تواند خودش این چرخه را طی کند اگر رمز را هم بداند. کارِ
 * این گیت جلوگیری از حذفِ حساب با یک لمسِ اتفاقی یا توسط کسی است که فقط
 * گوشیِ قفل‌نشده را برداشته — نه دفاع در برابرِ کسی که رمز را دارد.
 *
 * سنِ نشان محدود است تا یک «تایید»ِ فراموش‌شده دو روز بعد هنوز معتبر نباشد.
 */
import { storage } from '../platform.ts';
import { reportError } from './report.ts';

const DEST_KEY = 'auth.afterDest';
const REAUTH_KEY = 'auth.deleteReauth';

/** بعد از این مدت، «دوباره وارد شدم» دیگر تاییدِ همین حالا نیست. */
export const DELETE_REAUTH_TTL_MS = 10 * 60_000;

/**
 * مقصدِ قابل‌قبول: یک مسیرِ داخلی و بس.
 *
 * `//evil.com` و `https://evil.com` هر دو برای مرورگر آدرسِ **بیرونی**اند.
 * این مقدار از آدرسِ صفحه هم خوانده می‌شود (`?from=`)، یعنی هرکسی می‌تواند
 * لینکی بسازد که کاربر بعد از ورود جای دیگری سر دربیاورد.
 */
export const isSafeDest = (raw: string | null | undefined): raw is string =>
  typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//');

/** «بعد از ورود، من را ببر این‌جا.» بی‌صدا رد می‌شود اگر مسیر داخلی نباشد. */
export async function rememberAfterAuth(dest: string): Promise<void> {
  if (!isSafeDest(dest)) return;
  try {
    await storage().setItem(DEST_KEY, dest);
  } catch (e) {
    // نرسیدن به مقصد آزاردهنده است، ولی نباید خودِ ورود را بشکند.
    reportError('afterAuth.remember', e);
  }
}

/** مقصدِ ذخیره‌شده، و همان‌جا پاکش می‌کند — یک‌بارمصرف، عمداً. */
export async function takeAfterAuth(): Promise<string | null> {
  try {
    const dest = await storage().getItem(DEST_KEY);
    if (dest) await storage().removeItem(DEST_KEY);
    return isSafeDest(dest) ? dest : null;
  } catch (e) {
    reportError('afterAuth.take', e);
    return null;
  }
}

/** یک تاییدِ نیمه‌کاره که کاربر رهایش کرده، نباید تا ابد بماند. */
export async function clearDeleteReauth(): Promise<void> {
  try {
    await storage().removeItem(REAUTH_KEY);
  } catch (e) {
    reportError('afterAuth.clearReauth', e);
  }
}

/**
 * «دارم برای تاییدِ حذف خارج می‌شوم» — درست قبل از `signOut`.
 *
 * دلیلِ اختیاریِ کاربر هم همراهش می‌رود: او یک بار نوشته‌اش، و از دست دادنش
 * فقط به این خاطر که وسطش لازم بود دوباره وارد شود، بی‌احترامی به همان چند
 * خطی است که خواسته‌ایم بنویسد.
 */
export async function armDeleteReauth(reason = ''): Promise<void> {
  try {
    await storage().setItem(REAUTH_KEY, JSON.stringify({ at: Date.now(), reason }));
  } catch (e) {
    reportError('afterAuth.armReauth', e);
  }
}

/** نتیجه‌ی برگشت از آن خروج: تایید شد یا نه، و دلیلی که نوشته بود. */
export interface DeleteReauth {
  ok: boolean;
  reason: string;
}

/**
 * آیا این ورود همان تاییدِ حذف بود — و یک‌بارمصرف، مثل مقصد.
 *
 * `now` پارامتر است تا سنجشِ انقضا بدونِ دست بردن در ساعتِ سیستم تست شود.
 */
export async function takeDeleteReauth(now: number = Date.now()): Promise<DeleteReauth> {
  const none: DeleteReauth = { ok: false, reason: '' };
  try {
    const raw = await storage().getItem(REAUTH_KEY);
    if (!raw) return none;
    await storage().removeItem(REAUTH_KEY);
    const saved = JSON.parse(raw) as { at?: unknown; reason?: unknown };
    const at = typeof saved.at === 'number' ? saved.at : 0;
    // ساعتِ عقب‌رفته (`now < at`) هم نامعتبر است، نه یک تاییدِ ابدی.
    if (at <= 0 || now < at || now - at > DELETE_REAUTH_TTL_MS) return none;
    return { ok: true, reason: typeof saved.reason === 'string' ? saved.reason : '' };
  } catch (e) {
    reportError('afterAuth.takeReauth', e);
    return none;
  }
}
